import { useState, useEffect } from "react";
import { X, Download, FileSpreadsheet, FileText, FileType, CalendarDays, FileArchive } from "lucide-react";
import { format } from "date-fns";
import { pl } from "date-fns/locale";
import { API } from "@/lib/api";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { useI18n } from "@/context/I18nContext";

function DateField({ label, value, onChange, testid }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const d = value ? new Date(value + "T00:00:00") : undefined;
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[11px] text-slate-300">{label}</label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button data-testid={testid} className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-left min-w-[120px] flex items-center gap-1.5 hover:border-slate-500">
            <CalendarDays size={12} className="text-slate-400" />
            {d ? format(d, "dd.MM.yyyy") : <span className="text-slate-500">{t("dd.mm.rrrr")}</span>}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="single" locale={pl} weekStartsOn={1} selected={d} onSelect={(nd) => { onChange(nd ? format(nd, "yyyy-MM-dd") : ""); setOpen(false); }} initialFocus />
        </PopoverContent>
      </Popover>
    </div>
  );
}

const REPORTS = [
  { key: "fibers", label: "Włókna (wolne/zajęte)" },
  { key: "clients", label: "Lista klientów" },
  { key: "cables", label: "Kable" },
  { key: "length_balance", label: "Bilans długości" },
  { key: "splitters", label: "Zajętość splitterów" },
  { key: "faults", label: "Awarie" },
  { key: "power_alerts", label: "Alerty mocy klientów" },
];

export default function ReportsPanel({ onClose }) {
  const { t } = useI18n();
  const [active, setActive] = useState("fibers");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState("");
  const [flt, setFlt] = useState({ date_from: "", date_to: "", cable_type: "", placement: "", locality: "" });

  const qs = () => {
    if (active !== "length_balance") return "";
    const p = new URLSearchParams();
    Object.entries(flt).forEach(([k, v]) => { if (v) p.append(k, v); });
    const s = p.toString();
    return s ? `?${s}` : "";
  };

  useEffect(() => {
    setLoading(true); setError("");
    const h = setTimeout(() => {
      api.get(`/reports/${active}${qs()}`)
        .then((r) => setData(r.data))
        .catch(() => { setData(null); setError(t("Nie udało się wczytać raportu")); })
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, flt]);

  const exportFile = async (fmt) => {
    setExporting(fmt);
    try {
      const sep = qs() ? "&" : "?";
      const res = await fetch(`${API}/reports/${active}/export${qs()}${sep}fmt=${fmt}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `raport_${active}.${fmt}`; a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError(t("Eksport nie powiódł się"));
    } finally {
      setExporting("");
    }
  };

  const exportInvestor = async () => {
    setExporting("investor");
    try {
      const p = new URLSearchParams();
      Object.entries(flt).forEach(([k, v]) => { if (v) p.append(k, v); });
      p.append("fmt", "pdf");
      const res = await fetch(`${API}/investor-package?${p.toString()}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "pakiet_inwestora.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError(t("Nie udało się wygenerować pakietu"));
    } finally {
      setExporting("");
    }
  };

  return (
    <div className="absolute inset-0 bg-slate-900/98 backdrop-blur flex flex-col" data-testid="reports-panel">
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700">
        <h2 className="font-heading font-bold text-lg">{t("Raporty")}</h2>
        <button onClick={onClose} data-testid="reports-close" className="text-slate-400 hover:text-white"><X size={20} /></button>
      </div>
      <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-800 flex-wrap">
        {REPORTS.map((r) => (
          <button key={r.key} onClick={() => setActive(r.key)} data-testid={`report-tab-${r.key}`}
            className={`px-3 py-1.5 rounded text-sm ${active === r.key ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-300 hover:bg-slate-700"}`}>
            {t(r.label)}
            {r.label}
          </button>
        ))}
        <div className="ml-auto flex gap-2">
          <button onClick={() => exportFile("xlsx")} disabled={!!exporting} data-testid="export-xlsx" className="px-3 py-1.5 rounded text-sm bg-green-700 hover:bg-green-600 disabled:opacity-50 flex items-center gap-1.5"><FileSpreadsheet size={14} /> Excel</button>
          <button onClick={() => exportFile("csv")} disabled={!!exporting} data-testid="export-csv" className="px-3 py-1.5 rounded text-sm bg-slate-700 hover:bg-slate-600 disabled:opacity-50 flex items-center gap-1.5"><FileType size={14} /> CSV</button>
          <button onClick={() => exportFile("pdf")} disabled={!!exporting} data-testid="export-pdf" className="px-3 py-1.5 rounded text-sm bg-red-700 hover:bg-red-600 disabled:opacity-50 flex items-center gap-1.5"><FileText size={14} /> PDF</button>
        </div>
      </div>
      <div className="flex-1 overflow-auto p-5">
        {active === "length_balance" && (
          <div className="mb-4 flex flex-wrap gap-2 items-end bg-slate-800 border border-slate-700 rounded-lg p-3" data-testid="length-filters">
            <DateField label={t("Data od")} value={flt.date_from} onChange={(v) => setFlt((f) => ({ ...f, date_from: v }))} testid="filter-date-from" />
            <DateField label={t("Data do")} value={flt.date_to} onChange={(v) => setFlt((f) => ({ ...f, date_to: v }))} testid="filter-date-to" />
            <div className="flex flex-col gap-1">
              <label className="text-[11px] text-slate-300">{t("Typ kabla")}</label>
              <select value={flt.cable_type} onChange={(e) => setFlt((f) => ({ ...f, cable_type: e.target.value }))} data-testid="filter-cable-type"
                className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs min-w-[120px]">
                <option value="">{t("Wszystkie")}</option>
                {(data?.filters?.cable_types || []).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[11px] text-slate-300">{t("Umieszczenie")}</label>
              <select value={flt.placement} onChange={(e) => setFlt((f) => ({ ...f, placement: e.target.value }))} data-testid="filter-placement"
                className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs min-w-[120px]">
                <option value="">{t("Wszystkie")}</option>
                {(data?.filters?.placements || []).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[11px] text-slate-300">{t("Miejscowość")}</label>
              <input type="text" value={flt.locality} onChange={(e) => setFlt((f) => ({ ...f, locality: e.target.value }))} data-testid="filter-locality"
                placeholder={t("szukaj…")} className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs" />
            </div>
            <button onClick={() => setFlt({ date_from: "", date_to: "", cable_type: "", placement: "", locality: "" })} data-testid="filter-clear"
              className="text-xs text-slate-400 hover:text-white underline py-1">{t("Wyczyść")}</button>
            <button onClick={exportInvestor} disabled={!!exporting} data-testid="export-investor"
              className="ml-auto px-3 py-1.5 rounded text-sm bg-amber-600 hover:bg-amber-500 disabled:opacity-50 flex items-center gap-1.5 text-white self-end">
              <FileArchive size={14} /> {exporting === "investor" ? t("Generuję…") : t("Pakiet inwestora (PDF)")}
            </button>
          </div>
        )}
        {error && <div className="mb-3 text-sm text-red-400" data-testid="reports-error">{error}</div>}
        {loading ? <div className="text-slate-500">{t("Ładowanie…")}</div> : data && (
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-slate-800">
                {data.columns.map((c) => <th key={c} className="text-left px-3 py-2 border border-slate-700 font-heading">{c}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row, i) => {
                const isTotal = String(row[data.columns[0]] ?? "").toUpperCase() === "RAZEM";
                return (
                  <tr key={i} className={isTotal ? "bg-slate-800 font-bold border-t-2 border-blue-500" : (i % 2 ? "bg-slate-900" : "bg-slate-950")} data-testid={isTotal ? "report-total-row" : undefined}>
                    {data.columns.map((c) => <td key={c} className={`px-3 py-1.5 border border-slate-800 font-mono text-xs ${isTotal ? "text-cyan-300" : "text-slate-200"}`}>{String(row[c] ?? "")}</td>)}
                  </tr>
                );
              })}
              {data.rows.length === 0 && <tr><td colSpan={data.columns.length} className="text-center text-slate-500 py-6">{t("Brak danych")}</td></tr>}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
